import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Loader2 } from "lucide-react";
import { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";

interface ProductCardProps {
  product: ShopifyProduct;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  
  const { node } = product;
  const image = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;
  const firstVariant = node.variants.edges[0]?.node;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!firstVariant) return;
    
    await addItem({
      product,
      variantId: firstVariant.id,
      variantTitle: firstVariant.title,
      price: firstVariant.price,
      quantity: 1,
      selectedOptions: firstVariant.selectedOptions || []
    });
    
    toast.success("Added to cart", {
      description: node.title,
    });
  };

  return (
    <Link 
      to={`/product/${node.handle}`}
      className="product-card group cursor-pointer block"
    >
      <div className="relative aspect-[3/4] bg-background rounded-sm overflow-hidden mb-4">
        {image ? (
          <img
            src={image.url ? `${image.url}&width=800` : image.url}
            alt={image.altText || `${node.title} Premium Vegan leather perfume atomizer`}
            width={400}
            height={533}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-secondary flex items-center justify-center">
            <span className="text-muted-foreground">No image</span>
          </div>
        )}
        {/* Hover overlay with Add to Cart */}
        <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors duration-300 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100">
          <Button 
            onClick={handleAddToCart}
            disabled={isLoading || !firstVariant?.availableForSale}
            className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
            size="sm"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ShoppingBag className="w-4 h-4 mr-2" />
                Add to Cart
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="text-center">
        <h3 className="font-serif text-xl lg:text-2xl text-foreground mb-1 tracking-wide">
          {node.title}
        </h3>
        <p className="text-sm text-muted-foreground font-light mb-2">
          {node.description?.split(".")[0] || "10ml premium vegan leather atomizer"}
        </p>
        <p className="text-foreground font-medium">
          {price.currencyCode} {parseFloat(price.amount).toFixed(2)}
        </p>
      </div>
    </Link>
  );
};
